import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Alert, Button, Col, Form, Row } from 'react-bootstrap';
import { format, parse } from 'date-fns';
import Cookies from 'js-cookie';
import { cardNumberFormatter, expirationDateFormatter } from '../../utils/CheckoutUtils';
import useBookingData from '../../state/booking/hooks/useProducer';

export default function CheckoutForm({ roomId, rateData, personalInformation, paymentSuccess }) {
  const [cardNumber, setCardNumber] = useState('');
  const [expireDate, setExpireDate] = useState('');
  const [isSubmit, setIsSubmit] = useState(false);
  const [processErrors, setProcessErrors] = useState({});
  const { booking } = useBookingData();
  const submitButton = () => {
    if (isSubmit) {
      return (
        <Button variant="primary" type="button" size="lg" className="btn-select">
          Processing Booking
        </Button>
      );
    }
    return (
      <Button variant="primary" type="submit" size="lg" className="btn-select">
        Book This Room
      </Button>
    );
  };
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  const onSubmitPayment = ({ holderName, cvc }) => {
    // eslint-disable-next-line prefer-rest-params
    setIsSubmit(true);
    const expireDateArray = expireDate.split('/');
    const url = `${process.env.API_URL}/hotel/booking/`;
    const fetchData = async () => {
      try {
        const bookingData = {
          email: personalInformation.email,
          name_first: personalInformation.firstName,
          name_last: personalInformation.lastName,
          address: personalInformation.address,
          city: personalInformation.city,
          country_code: personalInformation.country,
          phone_number: personalInformation.phoneNumber.replace(/[^\d.-]/g, ''),
          booking_request_id: rateData.ppn_bundle,
          card_holder: holderName,
          expires_year: '20'.concat(expireDateArray[1]),
          expires_month: expireDateArray[0],
          cvc_code: cvc,
          post_code: personalInformation.zipCode,
          card_number: cardNumber.replace(/ /g, ''),
          start_date: format(parse(booking.check_in, 'MM/dd/yyyy', new Date()), 'yyyy-MM-dd'),
          end_date: format(parse(booking.check_out, 'MM/dd/yyyy', new Date()), 'yyyy-MM-dd'),
          external_hotel_id: booking.hotel_id,
          external_room_id: roomId,
        };
        const cookieValue = Cookies.get('csrftoken');
        const response = await fetch(url, {
          method: 'POST', // or 'PUT'
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': cookieValue,
          },
          body: JSON.stringify(bookingData),
        });
        const json = await response.json();
        if (response.status >= 400 && response.status < 600) {
          Object.keys(json).forEach((key) => {
            if (Array.isArray(json[key])) {
              setProcessErrors({ ...processErrors, 'Not Correct': json[key][0] });
            } else {
              setProcessErrors({ ...processErrors, [key]: json[key] });
            }
          });
        }
        if (response.status === 200) {
          // mixpanel.track('Benefit Link Clicked', { 'Benefit Type': 'Hotel', 'Benefit Name': 'book hotel' });
          paymentSuccess(json);
        }
        setIsSubmit(false);
      } catch (error) {
        setIsSubmit(false);
        const errorsMessage = { 'Processing Error': 'There was a problem during the reservation, please contact the Concierge Team.' };
        setProcessErrors({ ...processErrors, ...errorsMessage });
      }
    };
    fetchData();
  };
  return (
    <>
      <Form onSubmit={handleSubmit(onSubmitPayment)} className="p-4">
        <Row>
          <Col md={12}>
            <Form.Group className="mb-3" controlId="hotel-holderName">
              <Form.Label>Card Holder Name</Form.Label>
              <Form.Control type="text" className="hotel-input-white" isInvalid={!!errors.holderName} placeholder="Card Holder Name" {...register('holderName', { required: true })} />
              {errors?.holderName?.type === 'required' && <p className="invalid-feedback">This field is required</p>}
            </Form.Group>
          </Col>
        </Row>
        <Row>
          <Col md={6} sm={12}>
            <Form.Group className="mb-3" controlId="hotel-cardNumber">
              <Form.Control
                type="text"
                className="hotel-input-white"
                isInvalid={!!errors.cardNumber}
                placeholder="Credit Card Number"
                {...register('cardNumber', {
                  required: true,
                  onChange: (e) => {
                    const newValue = cardNumberFormatter(cardNumber, e.target.value);
                    setValue('cardNumber', newValue, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    setCardNumber(newValue);
                  },
                })}
              />
              {errors?.cardNumber?.type === 'required' && <p className="invalid-feedback">Credit Card Number is required</p>}
              {errors?.cardNumber?.type === 'maxLength' && <p className="invalid-feedback">Credit Card Number cannot exceed 20 characters</p>}
              {errors?.cardNumber?.type === 'pattern' && <p className="invalid-feedback">Alphabetical characters only</p>}
            </Form.Group>
          </Col>
          <Col md={3} xs={6}>
            <Form.Group className="mb-3" controlId="hotel-expDate">
              <Form.Control
                type="text"
                className="hotel-input-white"
                isInvalid={!!errors.expDate}
                placeholder="MM / YY"
                {...register('expDate', {
                  required: true,
                  onChange: (e) => {
                    const newValue = expirationDateFormatter(expireDate, e.target.value);
                    setValue('expDate', newValue, {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                    setExpireDate(newValue);
                  },
                })}
              />
              {errors?.expDate?.type === 'required' && <p className="invalid-feedback">Expire Date is required</p>}
            </Form.Group>
          </Col>
          <Col md={3} xs={6}>
            <Form.Group className="mb-3" controlId="hotel-cvc">
              <Form.Control
                type="text"
                className="hotel-input-white"
                isInvalid={!!errors.cvc}
                placeholder="CVC"
                {...register('cvc', {
                  required: true,
                  pattern: /^[0-9]*$/i,
                  maxLength: 5,
                  onChange: (e) => {
                    setValue('cvc', e.target.value.replace(/[^\d]/g, ''), {
                      shouldValidate: true,
                      shouldDirty: true,
                    });
                  },
                })}
              />
              {errors?.cvc?.type === 'required' && <p className="invalid-feedback">CVC is required</p>}
              {errors?.cvc?.type === 'maxLength' && <p className="invalid-feedback">CVC cannot exceed 5 characters</p>}
              {errors?.cvc?.type === 'pattern' && <p className="invalid-feedback">Numeric characters only</p>}
            </Form.Group>
          </Col>
        </Row>
        <div className="d-grid gap-2">
          {Object.keys(processErrors).map((key) => {
            return (
              <Alert variant="danger" onClick={() => setProcessErrors({})} key={Math.random()}>
                <p>{processErrors[key]}</p>
              </Alert>
            );
          })}
        </div>
        <div className="d-grid gap-2">{submitButton()}</div>
      </Form>
    </>
  );
}
